import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Play, Plus, Trash2 } from 'lucide-react';
import { api } from '@/api';
import { formatDate, formatTime } from '@/utils/helpers';
import { TeamNameAutocomplete } from './AdminShared';
import { SportIcon, toLocalDatetimeInput } from './adminGameHelpers';

export const AdminFixtures = ({ fixtures, leagues, sports, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingFixture, setEditingFixture] = useState(null);
  const [formData, setFormData] = useState({
    league_id: '', home_team_name: '', away_team_name: '', scheduled_time: '', venue: '', has_coverage: false, video_sources: []
  });
  const [newSource, setNewSource] = useState({ type: 'youtube', url: '', is_free: true, embed_url: '' });

  const resetForm = () => {
    setFormData({ league_id: '', home_team_name: '', away_team_name: '', scheduled_time: '', venue: '', has_coverage: false, video_sources: [] });
    setEditingFixture(null);
    setShowForm(false);
  };

  const handleEdit = (fixture) => {
    setEditingFixture(fixture);
    setFormData({
      league_id: fixture.league_id || '',
      home_team_name: fixture.home_team_name || '',
      away_team_name: fixture.away_team_name || '',
      scheduled_time: toLocalDatetimeInput(fixture.scheduled_time),
      venue: fixture.venue || '',
      has_coverage: fixture.has_coverage || false,
      video_sources: fixture.video_sources || []
    });
    setShowForm(true);
  };

  const addVideoSource = () => {
    if (newSource.url) {
      setFormData({
        ...formData,
        video_sources: [...formData.video_sources, { ...newSource }]
      });
      setNewSource({ type: 'youtube', url: '', is_free: true, embed_url: '' });
    }
  };

  const removeVideoSource = (index) => {
    setFormData({
      ...formData,
      video_sources: formData.video_sources.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        scheduled_time: new Date(formData.scheduled_time).toISOString()
      };
      if (editingFixture) {
        await api.updateFixture(editingFixture.id, payload);
      } else {
        await api.createFixture(payload);
      }
      resetForm();
      onRefresh();
    } catch (error) {
      alert('Error saving fixture');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this fixture?')) {
      try {
        await api.deleteFixture(id);
        onRefresh();
      } catch (error) {
        alert('Error deleting fixture');
      }
    }
  };

  const getSportForLeague = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return sports.find(s => s.id === league?.sport_id);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Fixtures ({fixtures.length})</h2>
        <Button onClick={() => setShowForm(true)} className="bg-sky-300 hover:bg-sky-400">
          <Plus className="w-4 h-4 mr-2" /> Add Fixture
        </Button>
      </div>

      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">Add New Fixture</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">League</Label>
                  <Select value={formData.league_id} onValueChange={(v) => setFormData({ ...formData, league_id: v })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select League" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {leagues.map(league => {
                        const sport = getSportForLeague(league.id);
                        return (
                          <SelectItem key={league.id} value={league.id} className="text-white hover:bg-gray-700">
                            <span className="flex items-center gap-1.5"><SportIcon sport={sport} className="w-3.5 h-3.5 inline" /> {league.name}</span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Scheduled Time</Label>
                  <Input 
                    type="datetime-local"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Home Team Name</Label>
                  <TeamNameAutocomplete
                    value={formData.home_team_name}
                    onChange={(v) => setFormData({ ...formData, home_team_name: v })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Home Team"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Away Team Name</Label>
                  <TeamNameAutocomplete
                    value={formData.away_team_name}
                    onChange={(v) => setFormData({ ...formData, away_team_name: v })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Away Team"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Venue</Label>
                  <Input 
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Venue Name"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input 
                    type="checkbox" 
                    checked={formData.has_coverage}
                    onChange={(e) => setFormData({ ...formData, has_coverage: e.target.checked })}
                  />
                  <Label className="text-gray-300">Has Coverage</Label>
                </div>
              </div>
              
              {/* Video Sources Section */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <Label className="text-gray-300 mb-2 block">Video Sources (for livestream)</Label>
                
                {/* Existing sources */}
                {formData.video_sources.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.video_sources.map((source, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-700/50 p-2 rounded">
                        <span className="text-xs text-sky-300 font-mono uppercase">{source.type}</span>
                        <span className="text-gray-300 text-sm truncate flex-1">{source.url}</span>
                        {source.is_free && <Badge className="bg-green-500/50 text-green-200">Free</Badge>}
                        <Button size="sm" variant="ghost" onClick={() => removeVideoSource(index)}>
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add new source */}
                <div className="flex items-end gap-2">
                  <div className="w-32">
                    <Select value={newSource.type} onValueChange={(v) => setNewSource({ ...newSource, type: v })}>
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="youtube" className="text-white hover:bg-gray-700">YouTube</SelectItem>
                        <SelectItem value="uscreen" className="text-white hover:bg-gray-700">Plus</SelectItem>
                        <SelectItem value="facebook" className="text-white hover:bg-gray-700">Facebook</SelectItem>
                        <SelectItem value="twitch" className="text-white hover:bg-gray-700">Twitch</SelectItem>
                        <SelectItem value="other" className="text-white hover:bg-gray-700">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Input 
                      value={newSource.url}
                      onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white text-sm"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <input 
                      type="checkbox" 
                      checked={newSource.is_free}
                      onChange={(e) => setNewSource({ ...newSource, is_free: e.target.checked })}
                    />
                    <span className="text-gray-400 text-xs">Free</span>
                  </div>
                  <Button type="button" size="sm" onClick={addVideoSource} className="bg-sky-500 hover:bg-sky-600">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400">{editingFixture ? 'Update' : 'Save'} Fixture</Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {fixtures.map(fixture => (
          <Card key={fixture.id} className="bg-gray-800/50 border-sky-900/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm text-sky-200">{fixture.sport_name} / {fixture.league_name}</span>
                  {fixture.has_coverage && <Badge className="bg-green-500">Coverage</Badge>}
                  {fixture.video_sources?.length > 0 && (
                    <Badge className="bg-red-500 flex items-center gap-1">
                      <Play className="w-3 h-3" /> {fixture.video_sources.length} stream{fixture.video_sources.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                <h3 className="text-white font-semibold">{fixture.home_team_name} vs {fixture.away_team_name}</h3>
                <p className="text-sm text-gray-400">
                  {formatDate(fixture.scheduled_time)} at {formatTime(fixture.scheduled_time)}
                  {fixture.venue && ` • ${fixture.venue}`}
                </p>
                {fixture.video_sources?.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {fixture.video_sources.map((vs, i) => (
                      <span key={i} className="text-xs bg-gray-700 px-2 py-0.5 rounded text-gray-300">
                        {vs.type}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(fixture)}>
                  <Edit className="w-4 h-4 text-gray-400" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(fixture.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {fixtures.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No fixtures yet. Add upcoming games to display!
          </div>
        )}
      </div>
    </div>
  );
};
